<script setup lang="ts">
import type { CriteriaInstance } from "~/types/Checklist";

const props = defineProps<{
  instance: CriteriaInstance;
  instanceIndex: number;
  sectionPath: string;
}>();

const documentId = inject("documentId") as string;
const referenceChecklistStore = useAssignmentReferenceChecklist(documentId)();
const { checklist, excusedCriteria } = storeToRefs(referenceChecklistStore);

const definition = computed(() =>
  referenceChecklistStore.criterionDefinition(props.instance)
);
const label = computed(
  () => props.instance.labelOverride || definition.value?.label || "Checklist item"
);
const key = computed(() =>
  referenceChecklistStore.criterionKey(
    props.sectionPath,
    props.instance,
    props.instanceIndex
  )
);
const isIgnored = computed(() => excusedCriteria.value.includes(label.value));

const checkboxCount = computed(() => {
  if (props.instance.evaluationMethod === "paragraph") {
    return Math.max(props.instance.paragraphPositionsData?.length ?? 1, 1);
  }

  if (
    props.instance.evaluationMethod === "instance" &&
    Number(props.instance.maxPoints) > 0 &&
    Number(props.instance.pointsPerInstance) > 0
  ) {
    return Math.max(
      Math.ceil(
        Number(props.instance.maxPoints) /
          Number(props.instance.pointsPerInstance)
      ),
      1
    );
  }

  return 1;
});

const instructions = computed(() => {
  const criteria = definition.value;
  if (!criteria) return [];

  const steps = (criteria.selfAssessmentSteps ?? [])
    .map((step) => ({
      step: step.label,
      instructions: step.instructions,
    }))
    .filter((step) => step.instructions != undefined);

  if (steps.length > 0) return steps;

  const levelOverride = criteria.studentInstructionsOverrides?.find(
    (override) => override.levelIds.includes(checklist.value?.levelId ?? "")
  );
  const studentInstructions =
    levelOverride?.instructions ?? criteria.studentInstructions;

  return studentInstructions == undefined
    ? []
    : [{ step: "Student Notes", instructions: studentInstructions }];
});

const showInstructions = ref(false);
const toggleSingle = () => {
  if (isIgnored.value || checkboxCount.value !== 1) return;
  referenceChecklistStore.toggleComplete(key.value);
};
</script>

<template>
  <div class="mb-1 flex flex-col">
    <div
      class="flex min-h-[30px] cursor-pointer flex-row items-start justify-between rounded px-2 py-1 hover:bg-light-default"
      :class="{
        'bg-success-default bg-opacity-10':
          checkboxCount === 1 && referenceChecklistStore.isComplete(key),
        'cursor-default line-through': isIgnored,
      }"
      @click="toggleSingle"
    >
      <span class="inline grow select-none leading-[30px]">
        {{ label }}
        <button
          v-if="instructions.length > 0"
          type="button"
          class="mb-[1px] inline-flex items-center justify-center rounded p-[2px] align-middle leading-none text-primary hover:bg-primary/20"
          @click.stop="showInstructions = !showInstructions"
        >
          <BaseIconCircleHelp />
        </button>
      </span>

      <div
        v-if="!isIgnored"
        class="ml-4 flex h-[30px] flex-row items-center justify-center gap-[2px]"
      >
        <BaseCheckbox
          v-for="position in checkboxCount"
          :key="position"
          :model-value="
            checkboxCount === 1
              ? referenceChecklistStore.isComplete(key)
              : referenceChecklistStore.isComplete(key, position)
          "
          :class="{ 'pointer-events-none': checkboxCount === 1 }"
          @click.stop
          @update:model-value="
            checkboxCount === 1
              ? referenceChecklistStore.toggleComplete(key)
              : referenceChecklistStore.toggleComplete(key, position)
          "
        />
      </div>
    </div>

    <ChecklistCriteriaInstructions
      v-if="showInstructions && instructions.length > 0"
      :student-instructions="instructions"
    />
  </div>
</template>
